import React, { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import PublicHome from "../Dashboard/PublicHome";
import { isPublicFallCrawlHomepageEvent, sortLocalLiveUpdates } from "../../utils/localLive";
import FallCrawlHomepage from "./FallCrawlHomepage";

export const HOMEPAGE_CONFIG_PATH = "appConfig/homepage";
export const HOMEPAGE_MODES = Object.freeze({
  default: "default",
  fallCrawl: "fall-crawl",
});

export const shouldRenderFallCrawlHomepage = (config = {}, event = null) =>
  config.homepageMode === HOMEPAGE_MODES.fallCrawl &&
  Boolean(config.fallCrawlEventId) &&
  isPublicFallCrawlHomepageEvent(event || {});

const PublicHomepageSwitch = () => {
  const [fallCrawlData, setFallCrawlData] = useState(null);
  const [decisionState, setDecisionState] = useState("checking");

  useEffect(() => {
    let cancelled = false;

    const readPublicEventCollection = async (eventId, collectionName, itemLimit) => {
      const snapshot = await getDocs(query(
        collection(db, "localLiveEvents", eventId, collectionName),
        where("publicEnabled", "==", true),
        where("active", "==", true),
        limit(itemLimit)
      ));
      return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
    };

    const loadHomepage = async () => {
      try {
        const configSnapshot = await getDoc(doc(db, "appConfig", "homepage"));
        const config = configSnapshot.exists() ? configSnapshot.data() : {};

        if (config.homepageMode !== HOMEPAGE_MODES.fallCrawl || !config.fallCrawlEventId) {
          if (!cancelled) {
            setFallCrawlData(null);
            setDecisionState("default");
          }
          return;
        }

        const eventSnapshot = await getDoc(doc(db, "localLiveEvents", config.fallCrawlEventId));
        const event = eventSnapshot.exists()
          ? { id: eventSnapshot.id, ...eventSnapshot.data() }
          : null;

        if (!shouldRenderFallCrawlHomepage(config, event)) {
          if (!cancelled) {
            setFallCrawlData(null);
            setDecisionState("default");
          }
          return;
        }

        const [stops, vendors, updates] = await Promise.all([
          readPublicEventCollection(event.id, "stops", 50),
          readPublicEventCollection(event.id, "vendors", 100),
          readPublicEventCollection(event.id, "updates", 100),
        ]);

        if (!cancelled) {
          setFallCrawlData({
            event,
            stops,
            vendors,
            updates: sortLocalLiveUpdates(updates),
          });
          setDecisionState("fall-crawl");
        }
      } catch (error) {
        if (!cancelled) {
          setFallCrawlData(null);
          setDecisionState("default");
        }
      }
    };

    loadHomepage();

    return () => {
      cancelled = true;
    };
  }, []);

  if (decisionState === "checking") {
    return (
      <main className="public-homepage-loading-shell" aria-label="Loading homepage">
        <span />
      </main>
    );
  }

  if (decisionState === "fall-crawl" && fallCrawlData?.event) {
    return <FallCrawlHomepage {...fallCrawlData} />;
  }

  return <PublicHome />;
};

export default PublicHomepageSwitch;
